/**
 * Assina o `WS /stream` e alimenta a store da frota.
 *
 * ## Os dois casos que este hook existe para tratar
 *
 * 1. **Token vencido (código 4001).** A API autentica no upgrade e fecha a
 *    conexão quando o token vence. Reconectar com o MESMO token daria um laço
 *    de 401 — então aqui a resposta é *renovar e só depois voltar*.
 * 2. **Snapshot ausente.** Quando o Redis não tem o snapshot, a API manda
 *    `data: null` com um aviso. Mostrar "0 veículos" nesse caso seria dizer que
 *    a frota sumiu.
 */
import { useEffect, useRef } from "react";
import { getStreamUrl } from "../lib/api.ts";
import { streamMessageSchema } from "../lib/schemas.ts";
import { useAuthStore } from "../store/auth.store.ts";
import { useFleetStore } from "../store/fleet.store.ts";

/** Fechamento por token vencido. A faixa 4000–4999 é da aplicação. */
const CLOSE_TOKEN_EXPIRED = 4001;
const MAX_BACKOFF_MS = 30_000;

export function useFleetStream(): void {
  const token = useAuthStore((state) => state.token);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!token) return;

    const store = useFleetStore.getState();
    let socket: WebSocket | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let disposed = false;

    function connect(currentToken: string): void {
      if (disposed) return;
      store.setConnection(attemptsRef.current === 0 ? "connecting" : "reconnecting");
      socket = new WebSocket(getStreamUrl(currentToken));

      socket.onopen = () => {
        attemptsRef.current = 0;
        useFleetStore.getState().setConnection("live");
      };

      socket.onmessage = (event) => {
        const parsed = streamMessageSchema.safeParse(safeJson(event.data));
        if (!parsed.success) return;
        const message = parsed.data;
        const state = useFleetStore.getState();

        if (message.type === "snapshot") {
          if (!message.data) {
            state.setWarning(message.warning ?? "Snapshot indisponível no momento.");
            return;
          }
          state.applySnapshot(message.data.vehicles, message.data.generatedAt);
          return;
        }
        state.applyPositions(message.vehicles, message.at);
      };

      socket.onclose = async (event) => {
        if (disposed) return;

        if (event.code === CLOSE_TOKEN_EXPIRED) {
          // Renova ANTES de voltar. Sem isto, a reconexão levaria o token que
          // acabou de ser recusado, e o laço seria invisível: só um painel que
          // pisca "reconectando" para sempre.
          useFleetStore.getState().setConnection("reconnecting");
          const renewed = await useAuthStore.getState().refresh();
          const next = useAuthStore.getState().token;
          if (renewed && next && !disposed) connect(next);
          else useFleetStore.getState().setConnection("offline");
          return;
        }

        const wait = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attemptsRef.current++);
        useFleetStore.getState().setConnection("reconnecting");
        retryTimer = setTimeout(() => {
          const next = useAuthStore.getState().token;
          if (next) connect(next);
        }, wait);
      };

      socket.onerror = () => {
        /* o `close` a seguir traz o desfecho e decide o que fazer */
      };
    }

    connect(token);

    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      // Tira o handler antes de fechar: sem isso, desmontar o componente
      // dispararia a reconexão que o desmonte queria justamente evitar.
      if (socket) {
        socket.onclose = null;
        socket.close();
      }
      useFleetStore.getState().setConnection("idle");
    };
  }, [token]);
}

function safeJson(raw: unknown): unknown {
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
